<script lang="ts">
    import { onMount } from 'svelte';
    import { get, type Writable } from 'svelte/store';
    import { push } from 'svelte-spa-router';

    import CodeModule from './CodeModule.svelte';
    import { EDIT_ICON, RETRY_ICON } from './constants/loginImage';
    import { setFifaLoginFlowContext } from './context';
    import { MODULE_ENUM, type LoginModuleType } from './enums/loginModule';
    import Loading from './Loading.svelte';
    import PictureModule from './PictureModule.svelte';
    import SafePerception from './SafePerception.svelte';
    import {
        changeModule,
        currentModule,
        loginPhoneNumber,
        pageModuleEnterTime,
        pendingLoginModule
    } from './store/loginFlow';
    import type { LoginTrackBids, LoginTrackEvent, LoginRes } from './types';
    import { getModuleDuration } from './utils/trackHelper';

    export let bids: LoginTrackBids;
    export let trackEvent: LoginTrackEvent;
    export let phoneCellStore: Writable<string>;
    export let isJumpOneLink: Writable<boolean>;
    export let getRegisterExposeAttrs: () => Record<string, unknown> = () => ({});
    /** 登录子流程独立注册页曝光（默认关闭，避免与落地页重复上报） */
    export let shouldTrackRegisterExpose = false;
    export let ensurePassportSdk: () => void;
    export let normalizePassportLoginData: (
        data: (Partial<LoginRes> & { uid_str?: string }) | null | undefined
    ) => LoginRes | null;

    setFifaLoginFlowContext({
        bids,
        trackEvent,
        getRegisterExposeAttrs,
        phoneCellStore,
        isJumpOneLink,
        ensurePassportSdk,
        normalizePassportLoginData,
        editIcon: EDIT_ICON,
        retryIcon: RETRY_ICON
    });

    let previousModule: LoginModuleType | null = get(currentModule);
    let hasTrackedRegisterExpose = false;

    const trackModuleDuration = (
        module: LoginModuleType,
        enterTime: number | null
    ) => {
        if (!enterTime) {
            return;
        }

        const duration = getModuleDuration(enterTime);

        if (module === MODULE_ENUM.PICTURE) {
            trackEvent(bids.dvCodeTimeSw, { duration });
        } else if (module === MODULE_ENUM.CODE) {
            trackEvent(bids.verificationCodeTimeSw, { duration });
        }
    };

    // 与 MXLoginPage Login.svelte 一致：模块切换时上报上一模块停留时长并重置进入时间
    $: if ($currentModule) {
        const enterTime = get(pageModuleEnterTime);

        if (
            previousModule !== null &&
            previousModule !== $currentModule &&
            enterTime !== null
        ) {
            trackModuleDuration(previousModule, enterTime);
        }

        if (previousModule !== $currentModule) {
            previousModule = $currentModule;
            pageModuleEnterTime.set(window.performance.now());
        }
    }

    onMount(() => {
        isJumpOneLink.set(true);

        let phone = get(loginPhoneNumber);

        if (!phone) {
            phone = get(phoneCellStore) || '';

            if (phone) {
                loginPhoneNumber.set(phone);
            }
        }

        if (!phone) {
            push('/');

            return;
        }

        if (
            shouldTrackRegisterExpose &&
            !hasTrackedRegisterExpose &&
            bids.registerExpose
        ) {
            hasTrackedRegisterExpose = true;
            trackEvent(bids.registerExpose, getRegisterExposeAttrs());
        }

        const targetModule = get(pendingLoginModule) || MODULE_ENUM.CODE;

        if (get(currentModule) !== targetModule) {
            changeModule(targetModule);
        } else if (get(pageModuleEnterTime) === null) {
            pageModuleEnterTime.set(window.performance.now());
        }
    });
</script>

<div class="fifa-login-flow">
    <SafePerception />
    {#if $currentModule === MODULE_ENUM.PICTURE}
        <PictureModule />
    {:else if $currentModule === MODULE_ENUM.CODE}
        <CodeModule />
    {/if}
    <Loading />
</div>

<style lang="scss">
    .fifa-login-flow {
        width: 7.5rem;
        margin: 0 auto;
        overflow-y: auto;
        position: relative;
        box-sizing: border-box;
        overflow-x: hidden;
        font-family: DiDiSans-Pro-Regular;
        user-select: none;
        background-color: #ffffff;
        min-height: 100vh;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }
</style>
